import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Plus, Edit2, Trash2, Store } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import CreateProductModal from '@/components/Shop/CreateProductModal';

interface Product {
  id: string;
  name: string;
  description?: string;
  price: number;
  currency: string;
  image_url?: string;
  is_available: boolean;
  stock_quantity?: number;
  created_at: string;
}

interface Shop {
  id: string;
  shop_name: string;
  shop_description?: string;
}

export default function ShopManage() {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [shop, setShop] = useState<Shop | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    if (shopId && user) {
      fetchShopAndProducts();
    }
  }, [shopId, user]);

  const fetchShopAndProducts = async () => {
    try {
      // Fetch shop details
      const { data: shopData, error: shopError } = await supabase
        .from('user_shops')
        .select('id, shop_name, shop_description')
        .eq('id', shopId)
        .eq('user_id', user?.id)
        .single();

      if (shopError) throw shopError;
      setShop(shopData);

      // Fetch products
      const { data: productsData, error: productsError } = await supabase
        .from('shop_products')
        .select('*')
        .eq('shop_id', shopId)
        .order('created_at', { ascending: false });

      if (productsError) throw productsError;
      setProducts(productsData || []);

    } catch (error) {
      console.error('Error fetching shop:', error);
      toast.error('Errore nel caricamento del negozio');
      navigate('/app/shop');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (productId: string, productName: string) => {
    if (!confirm(`Sei sicuro di voler eliminare "${productName}"?`)) return;

    try {
      const { error } = await supabase
        .from('shop_products')
        .delete()
        .eq('id', productId);

      if (error) throw error;
      toast.success('Prodotto eliminato con successo');
      fetchShopAndProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Errore nell\'eliminazione del prodotto');
    }
  };

  const toggleProductAvailability = async (productId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('shop_products')
        .update({ is_available: !currentStatus })
        .eq('id', productId);

      if (error) throw error;
      toast.success(`Prodotto ${!currentStatus ? 'attivato' : 'disattivato'} con successo`);
      fetchShopAndProducts();
    } catch (error) {
      console.error('Error updating product:', error);
      toast.error('Errore nell\'aggiornamento del prodotto');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen gradient-secondary p-4">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="text-muted-foreground mt-4">Caricamento...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!shop) {
    return (
      <div className="min-h-screen gradient-secondary p-4">
        <div className="max-w-7xl mx-auto space-y-6">
          <Card className="glass border-white/10">
            <CardContent className="p-8 text-center">
              <Store className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Negozio non trovato</h3>
              <p className="text-muted-foreground mb-4">
                Non hai i permessi per gestire questo negozio
              </p>
              <Button onClick={() => navigate('/app/shop')} variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Torna ai Negozi
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-secondary">
      {/* Header */}
      <div className="glass border-b border-primary/20 sticky top-0 z-40 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/app/shop')}
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Indietro
              </Button>
              
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                  <Store className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">Gestisci {shop.shop_name}</h1>
                  <p className="text-muted-foreground">
                    {products.length} prodotti totali
                  </p>
                </div>
              </div>
            </div>

            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-primary hover:bg-primary/90"
            >
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi Prodotto
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto p-4 space-y-6">
        {/* Products Grid */}
        {products.length === 0 ? (
          <Card className="glass border-white/10">
            <CardContent className="p-8 text-center">
              <Store className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">
                Nessun prodotto ancora
              </h3>
              <p className="text-muted-foreground mb-4">
                Inizia aggiungendo i tuoi primi prodotti al negozio
              </p>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-primary hover:bg-primary/90"
              >
                <Plus className="w-4 h-4 mr-2" />
                Aggiungi il Primo Prodotto
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product) => (
              <Card key={product.id} className="glass border-white/10 overflow-hidden">
                <CardHeader className="p-0">
                  {product.image_url ? (
                    <div className="h-48 bg-gradient-to-br from-primary/20 to-secondary/20">
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="h-48 bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                      <Store className="w-16 h-16 text-primary" />
                    </div>
                  )}
                </CardHeader>

                <CardContent className="p-4 space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white">
                        {product.name}
                      </h3>
                      {product.description && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                          {product.description}
                        </p>
                      )}
                    </div>
                    <Badge variant={product.is_available ? "default" : "secondary"}>
                      {product.is_available ? 'Disponibile' : 'Non Disponibile'}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-2xl font-bold text-primary">
                        €{product.price.toFixed(2)}
                      </p>
                      {product.stock_quantity && (
                        <p className="text-sm text-muted-foreground">
                          Stock: {product.stock_quantity}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => toggleProductAvailability(product.id, product.is_available)}
                      className="flex-1"
                    >
                      {product.is_available ? 'Disattiva' : 'Attiva'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      onClick={() => handleDeleteProduct(product.id, product.name)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create Product Modal */}
      <CreateProductModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        shopId={shopId!}
        onProductCreated={fetchShopAndProducts}
      />
    </div>
  );
}